import { useEffect, useRef, type ReactNode } from "react";
import { X } from "lucide-react";

/** 居中对话框与右侧抽屉的公共行为：Escape 关闭、初始聚焦、简易焦点圈定。 */
function useOverlayBehavior(onClose: () => void, ref: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") { event.stopPropagation(); onClose(); return; }
      if (event.key !== "Tab" || !ref.current) return;
      const focusable = ref.current.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => { document.removeEventListener("keydown", onKeyDown); previous?.focus?.(); };
  }, [onClose, ref]);
}

/**
 * 唯一正式居中弹窗。高风险操作确认、创建表单等使用本组件；
 * 贴边详情使用 Drawer。禁止页面手写平行 dialog。
 */
export function Dialog({ title, description, onClose, children, className = "", wide = false }: {
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useOverlayBehavior(onClose, ref);
  return <div className="overlay-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <div className={`dialog ${wide ? "dialog-wide" : ""} ${className}`.trim()} role="dialog" aria-modal="true" aria-label={title} ref={ref} tabIndex={-1}>
      <header className="dialog-head">
        <div>
          <h2>{title}</h2>
          {description && <p>{description}</p>}
        </div>
        <button type="button" className="overlay-close" onClick={onClose} aria-label="关闭"><X size={18} /></button>
      </header>
      {children}
    </div>
  </div>;
}

/**
 * 唯一正式右侧抽屉。详情查看使用抽屉，不承载高风险确认（确认用 Dialog / ConfirmDialog）。
 */
export function Drawer({ title, description, onClose, children, footer, className = "" }: {
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useOverlayBehavior(onClose, ref);
  return <div className="overlay-backdrop drawer-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <div className={`drawer ${className}`.trim()} role="dialog" aria-modal="true" aria-label={title} ref={ref} tabIndex={-1}>
      <header className="dialog-head">
        <div>
          <h2>{title}</h2>
          {description && <p>{description}</p>}
        </div>
        <button type="button" className="overlay-close" onClick={onClose} aria-label="关闭"><X size={18} /></button>
      </header>
      <div className="drawer-body">{children}</div>
      {footer && <footer className="drawer-footer">{footer}</footer>}
    </div>
  </div>;
}
